import type React from 'react';
import { connect } from 'react-redux';

import { getInDomain } from '../../domains/externalUtils';
import * as ClientInfoUtils from '../../lib/ClientInfoUtils';
import * as RouteUtils from '../../lib/RouteUtils';
import * as UIUtils from '../../lib/UIStateUtils';
import breakpoints from '../../sass/definitions/_breakpoints.scss';
import sizes from '../../sass/definitions/_sizes.scss';
import * as Globals from '../../tools/Globals';

export interface IDetermineShouldDropSearchToNextLineProps {
  shouldDropSearchToNextLine: boolean;
}

const LandingContentWidths = Object.freeze({
  VIEW_PLANS_AND_SIGNUP_AND_LOGIN: 365,
} as const);

const ContentWidths = Object.freeze({
  ACTIVE_LANDING_SEARCH_BOX_HALF: 300 / 2,
  BROWSE: 90,
  DESKTOP_APP_HISTORY_BUTTONS: 100,
  LANDING_SEARCH_BOX_HALF: 410 / 2,
  NEW_SEARCH_BOX_HALF: 300 / 2,
  NOW_PLAYING_AND_MY_COLLECTION: 235,
  OLD_SEARCH_BOX_HALF: 200 / 2,
  RIGHT_RAIL: parseInt(sizes.RIGHT_RAIL_WIDTH, 10),
} as const);

function shouldDropToNextLine({
  isDesktopApp,
  isRightRailActive,
  windowWidth,
  isLandingPage,
  isSearchActive,
}: {
  isDesktopApp: boolean;
  isRightRailActive: boolean;
  windowWidth: number;
  isLandingPage: boolean;
  isSearchActive: boolean;
}): boolean {
  if (isLandingPage && !isSearchActive) {
    return Globals.matchMedia('(max-width: 1152px)').matches;
  }

  const activeWidths: number[] = isLandingPage
    ? [LandingContentWidths.VIEW_PLANS_AND_SIGNUP_AND_LOGIN]
    : [ContentWidths.NOW_PLAYING_AND_MY_COLLECTION];
  const rightRailHiddenWidth = parseInt(breakpoints.BREAK_RIGHT_RAIL_HIDDEN, 10);
  const rightRailFullSizeWidth = rightRailHiddenWidth + ContentWidths.RIGHT_RAIL;
  const landingSearchInputWidth = ContentWidths.ACTIVE_LANDING_SEARCH_BOX_HALF;

  !isLandingPage && activeWidths.push(ContentWidths.BROWSE);
  !isLandingPage && isDesktopApp && activeWidths.push(ContentWidths.DESKTOP_APP_HISTORY_BUTTONS);
  isLandingPage
    ? activeWidths.push(landingSearchInputWidth)
    : activeWidths.push(ContentWidths.NEW_SEARCH_BOX_HALF);
  let breakpoint = activeWidths.reduce((acc, val) => acc + val, 0) * 2;

  if (isRightRailActive) {
    if (windowWidth > rightRailHiddenWidth && windowWidth < rightRailFullSizeWidth) {
      const rightRailWidth = windowWidth - rightRailHiddenWidth;
      breakpoint = breakpoint + rightRailWidth;
    } else if (windowWidth >= rightRailFullSizeWidth) {
      breakpoint = breakpoint + ContentWidths.RIGHT_RAIL;
    }
  }

  return Globals.matchMedia(`(max-width: ${breakpoint}px)`).matches;
}

export function mapStateToProps(state: $TSFixMe): IDetermineShouldDropSearchToNextLineProps {
  const isDesktopApp = ClientInfoUtils.isElectron();
  const isRightRailActive = getInDomain('ads.utils.getIsRightRailActive', false)(state);
  const isLandingPage = RouteUtils.shouldShowLanding(state);
  const windowWidth = UIUtils.getScreenWidth(state);
  const isSearchActive = UIUtils.getIsMainSearchActive(state);
  const shouldDropSearchToNextLine = shouldDropToNextLine({
    isDesktopApp,
    isLandingPage,
    isRightRailActive,
    isSearchActive,
    windowWidth,
  });

  return {
    shouldDropSearchToNextLine,
  };
}

export default function determineShouldDropSearchToNextLine(
  Component: React.ComponentType<$TSFixMe>,
): $TSFixMe {
  return connect(mapStateToProps)(Component);
}
